/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT
 * Date:         2026-02-10
 * Modified By:  Claude Code
 *
 * Description:
 * Removed Refresh and Save Signature buttons. Signature saving is now
 * handled by the parent "Save and next" button via exposed ref method
 * getSignaturePayload(). The parent detects signature dirty state and
 * includes the captured image in the form submission payload.
 *
 * File Size Check:
 * - Current: 151
 * - Adding: ~-20 (removing buttons)
 * - Projected: ~131
 * - Decision: Proceed
 *
 * Related Files:
 * - client/src/components/setup/user-profile/UserProfileForm.tsx
 * - client/src/components/setup/user-profile/hooks/useUserForm.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SIGNATURE_TO_IMAGE_ON_SAVE
 * Date:         2026-02-04
 * Modified By:  Cursor
 *
 * Description:
 * Render signature HTML to PNG on save for reliable inline display in broadcast emails.
 */
import React, { useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import html2canvas from "html2canvas";
import BroadcastSignatureEditor, {
  type BroadcastSignatureEditorRef,
} from "../../broadcast-signature-editor/BroadcastSignatureEditor";

interface BroadcastSignatureProps {
  broadcastSignatureHtml: string;
  setBroadcastSignatureHtml: (html: string) => void;
  setIsSignatureDirty: (dirty: boolean) => void;
}

// [2026-02-10] MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT: expose payload method to parent
export interface BroadcastSignatureHandle {
  getSignaturePayload: () => Promise<{
    broadcastSignatureHtml: string;
    broadcastSignatureImage?: { data: string; contentType: string };
  }>;
}

const CAPTURE_STYLES: React.CSSProperties = {
  position: "absolute",
  left: "-9999px",
  width: "532px",
  padding: "16px",
  fontFamily: "Poppins, sans-serif", // Figma: Poppins only
  fontSize: "14px",
  color: "var(--color-black)",
  backgroundColor: "var(--color-white)",
  boxSizing: "border-box",
};

export const BroadcastSignature = forwardRef<BroadcastSignatureHandle, BroadcastSignatureProps>(({
  broadcastSignatureHtml,
  setBroadcastSignatureHtml,
  setIsSignatureDirty,
}, ref) => {
  const editorRef = useRef<BroadcastSignatureEditorRef | null>(null);
  const captureRef = useRef<HTMLDivElement | null>(null);

  const captureSignatureAsImage = useCallback(async (html: string): Promise<{ data: string; contentType: string } | null> => {
    if (!html?.trim() || !captureRef.current) return null;
    try {
      captureRef.current.innerHTML = html;

      // [2026-02-07] Wait for all images to load before capturing
      const images = captureRef.current.querySelectorAll("img");
      if (images.length > 0) {
        await Promise.all(
          Array.from(images).map(
            (img) =>
              new Promise<void>((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = () => resolve();
                  img.onerror = () => resolve();
                }
              })
          )
        );
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const canvas = await html2canvas(captureRef.current, {
        useCORS: true,
        logging: false,
        scale: 2,
        backgroundColor: "#ffffff",
      });
      const dataUrl = canvas.toDataURL("image/png");
      const match = dataUrl.match(/^data:image\/png;base64,(.+)$/);
      if (!match) return null;
      return { data: match[1], contentType: "image/png" };
    } catch {
      return null;
    }
  }, []);

  // [2026-02-10] MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT: expose method for parent to get payload
  useImperativeHandle(ref, () => ({
    getSignaturePayload: async () => {
      const getHtml = editorRef.current?.getHTML;
      const html = (getHtml?.() ?? broadcastSignatureHtml) || "";
      const broadcastSignatureImage = await captureSignatureAsImage(html);
      return {
        broadcastSignatureHtml: html,
        ...(broadcastSignatureImage && { broadcastSignatureImage }),
      };
    },
  }), [broadcastSignatureHtml, captureSignatureAsImage]);

  const handleEditorChange = React.useCallback((html: string) => {
    if (html !== broadcastSignatureHtml) {
      setBroadcastSignatureHtml(html);
      setIsSignatureDirty(true);
    }
  }, [broadcastSignatureHtml, setBroadcastSignatureHtml, setIsSignatureDirty]);

  return (
    <div className="mt-3">
      <div ref={captureRef} style={CAPTURE_STYLES} aria-hidden="true" />
      {/* [2026-02-10] MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT: removed Refresh + Save Signature buttons */}
      <BroadcastSignatureEditor
        ref={editorRef}
        value={broadcastSignatureHtml}
        // [2025-12-29] Type fix: html is string
        onChange={handleEditorChange}
      />
    </div>
  );
});
